import { useCallback, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { AgnesButton, AgnesLayout } from '@agnes/ui';
import type { AgnesSidebarItem, AgnesSidebarSection } from '@agnes/ui';
import { HelpCircle, LogOut } from 'lucide-react';
import { NeoAssist } from '@/components/NeoAssist';
import { PageTransition } from '@/components/motion/Motion';
import { MENU, ADMIN_MENU, type MenuItem } from '@/config/menu';
import ServiceAuthSSO from '@/services/ServiceAuthSSO';
import { useAuthStore } from '@/stores/useAuthStore';
import { usePermissionsStore } from '@/stores/usePermissionsStore';
import { useTheme } from '@/stores/useTheme';
import { fullName } from '@/utils/userDisplay';
import { BrandLogo } from '@/components/brand/BrandLogo';

// Converte o MenuItem (com `permission`) num AgnesSidebarItem, já filtrando pelas
// permissões do usuário — espelha o gate de rota (RequirePermission) pra o menu
// não exibir o que a rota bloqueia. Grupos caem quando não sobra nenhum filho.
function toSidebarItem(
  item: MenuItem,
  hasAny: (c: string | string[]) => boolean,
): AgnesSidebarItem | null {
  if (item.children?.length) {
    const children = item.children
      .map((child) => toSidebarItem(child, hasAny))
      .filter((child): child is AgnesSidebarItem => child !== null);
    if (!children.length) return null;
    return { label: item.label, path: item.path, icon: item.icon, children };
  }
  if (item.permission && !hasAny(item.permission)) return null;
  return { label: item.label, path: item.path, icon: item.icon, badge: item.badge };
}

function isActive(itemPath: string, currentPath: string): boolean {
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
}

export default function LayoutMain() {
  const location = useLocation();
  const navigate = useNavigate();

  const user = useAuthStore((s) => s.user);
  const getUser = useAuthStore((s) => s.getUser);
  const mode = useTheme((s) => s.mode);
  const permissions = usePermissionsStore((s) => s.permissions);
  const isSuper = usePermissionsStore((s) => s.isSuper);
  const loaded = usePermissionsStore((s) => s.loaded);
  const hasAnyPermission = usePermissionsStore((s) => s.hasAnyPermission);

  useEffect(() => {
    if (!user.firstName) getUser();
  }, [user.firstName, getUser]);

  useEffect(() => {
    usePermissionsStore.getState().fetchPermissions();
  }, []);

  const sections = useMemo<AgnesSidebarSection[]>(() => {
    const main = MENU.map((item) => toSidebarItem(item, hasAnyPermission)).filter(
      (item): item is AgnesSidebarItem => item !== null,
    );
    const admin = ADMIN_MENU.map((item) => toSidebarItem(item, hasAnyPermission)).filter(
      (item): item is AgnesSidebarItem => item !== null,
    );
    return [
      ...(main.length ? [{ items: main }] : []),
      ...(admin.length ? [{ label: 'Administração', items: admin }] : []),
    ];
    // hasAnyPermission lê o estado do store por fora do React — os state-slices
    // (permissions/isSuper/loaded) é que recompõem a árvore quando mudam.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, isSuper, loaded, hasAnyPermission]);

  const handleLogout = useCallback(async () => {
    await ServiceAuthSSO.signOut();
    usePermissionsStore.getState().reset();
    navigate('/login');
  }, [navigate]);

  return (
    <AgnesLayout
      logo={({ isOpen }) => (
        <AgnesButton
          className={isOpen ? 'w-full' : 'w-9 h-9 p-0'}
          variant="text"
          onClick={() => navigate('/home')}
        >
          <BrandLogo mode={isOpen ? 'default' : 'mark'} />
        </AgnesButton>
      )}
      headerHeight={56}
      theme={mode}
      onThemeChange={(next) => useTheme.setState({ mode: next })}
      avatar={{
        name: fullName(user, 'Meu perfil'),
        onClick: () => navigate('/perfil'),
      }}
      sections={sections}
      footerItems={[
        { label: 'Ajuda', icon: <HelpCircle className="h-5 w-5" />, path: '/central-de-ajuda' },
        { label: 'Sair', icon: <LogOut className="h-5 w-5" />, onClick: handleLogout },
      ]}
      sidebarWidthExpanded={260}
      pathname={location.pathname}
      onNavigate={navigate}
      isActive={isActive}
      className="text-agnes-text-primary"
    >
      {/* mode="wait": anima saída da rota antiga antes de entrar a nova.
          key por pathname para o AnimatePresence detectar a troca. */}
      <AnimatePresence mode="wait">
        <PageTransition key={location.pathname}>
          <Outlet />
        </PageTransition>
      </AnimatePresence>
      <NeoAssist />
    </AgnesLayout>
  );
}
